import { useSelector } from 'react-redux';
import { selectUser } from '@/store/bootstrap/selectors';

export const dateFormats = [
  { label: 'MM/DD/YYYY', value: 'MM-DD-YYYY' },
  { label: 'DD/MM/YYYY', value: 'DD-MM-YYYY' },
  { label: 'YYYY/MM/DD', value: 'YYYY-MM-DD' },
  { label: 'DD MMMM YYYY', value: 'DD MMMM YYYY' },
  { label: 'MMMM DD YYYY', value: 'MMMM DD YYYY' },
  { label: 'YYYY MMMM DD', value: 'YYYY MMMM DD' },
];

export const getDateFormat = (format: string): string => {
  switch (format) {
    case 'DD-MM-YYYY':
      return 'dd/MM/yyyy';
    case 'MM-DD-YYYY':
      return 'MM/dd/yyyy';
    case 'YYYY-MM-DD':
      return 'yyyy/MM/dd';
    case 'DD MMMM YYYY':
      return 'dd MMMM yyyy';
    case 'MMMM DD YYYY':
      return 'MMMM dd yyyy';
    case 'YYYY MMMM DD':
      return 'yyyy MMMM dd';
    default:
      return 'dd/MM/yyyy';
  }
};

/**
 * Returns a moment.js date+time format string from account date_fmt and time_fmt.
 * Use with moment.utc(iso).tz(timezone).format(result) to show in account timezone.
 */
export const getMomentDateTimeFormat = (
  dateFmt: string | undefined,
  timeFmt: string
): string => {
  const timePart = timeFmt === 'HH:mm' ? 'HH:mm' : 'h:mm A';
  switch (dateFmt) {
    case 'DD-MM-YYYY':
      return `DD/MM/YY, ${timePart}`;
    case 'MM-DD-YYYY':
      return `MM/DD/YY, ${timePart}`;
    case 'YYYY-MM-DD':
      return `YYYY/MM/DD, ${timePart}`;
    case 'DD MMMM YYYY':
      return `DD MMM YY, ${timePart}`;
    case 'MMMM DD YYYY':
      return `MMM DD YY, ${timePart}`;
    case 'YYYY MMMM DD':
      return `YYYY MMM DD, ${timePart}`;
    default:
      return `DD/MM/YY, ${timePart}`;
  }
};

export const useDateFormat = (sourceType: 'account' | null = null) => {
  const currUser = useSelector(selectUser);
  const source = sourceType === 'account' ? currUser?.account : currUser;

  const { date_fmt, time_fmt } = source || {};

  const currUserTimezone = currUser?.time_zone;

  const formatObj = dateFormats.find((format) => format.value === date_fmt);
  const timeFormat = time_fmt === 'hour_12' ? 'hh:mm A' : 'HH:mm';

  const resolvedDateFormat = formatObj || dateFormats[0];

  return {
    currUserTimezone,
    dateFormat: resolvedDateFormat,
    timeFormat,
    dateFnsFormat: getDateFormat(date_fmt),
    /** Moment format for date+time in account preference; use with moment.utc(iso).tz(currUserTimezone).format(momentDateTimeFormat) */
    momentDateTimeFormat: getMomentDateTimeFormat(date_fmt, timeFormat),
    /**
     * Moment/Day.js format that exactly matches the in-app scorecard submitted-time display
     * (e.g. "DD/MM/YYYY [at] hh:mm A" → "24/12/2025 at 06:47 PM").
     * Use this for the PDF "When" field so it is identical to the in-app tooltip.
     */
    scorecardDateTimeFormat: `${resolvedDateFormat?.label} [at] ${timeFormat}`,
  };
};
